import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Col, Input, Row, Select, Space, Switch, Typography, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText } from '../format';

const { Text } = Typography;

// Account-prefix lists, in the order the counter-lines are sorted.
const LIST_FIELDS = [
  'bankAccounts',
  'excludedOrderTypes',
  'groupPartners',
  'customerAccounts',
  'refundAccounts',
  'groupPayableAccounts',
  'capitalAccounts',
  'investmentAccounts',
  'leaseLiabilityAccounts',
  'leaseSupplierAccounts',
  'taxAccounts',
  'vatPayableAccounts',
  'outputVatAccounts',
];

/**
 * How the bank postings are sorted into the cash-flow lines. Every list holds
 * account prefixes (or partner codes, order types), typed and confirmed with
 * Enter or a comma.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Function} props.onSaved - Called after saving
 * @returns {JSX.Element} The settings form
 */
export default function CashFlowSettings({ companyId, onSaved }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ['client-reports-cash-flow-config', companyId],
    queryFn: () => API.clientReports.getCashFlowConfig(companyId),
  });

  useEffect(() => {
    if (!data?.data) return;
    setForm(data.data);
    setDirty(false);
  }, [data]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        groupName: form.groupName,
        ownerName: form.ownerName,
        vatGross: form.vatGross,
      };
      LIST_FIELDS.forEach((field) => {
        payload[field] = form[field];
      });
      return API.clientReports.saveCashFlowConfig(companyId, payload);
    },
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({
        queryKey: ['client-reports-cash-flow-config', companyId],
      });
      message.success(t('cashFlow.saved'));
      onSaved?.();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  if (!form) return null;

  const field = (name, control) => (
    <Col xs={24} md={12} key={name}>
      <div style={{ marginBottom: 12 }}>
        <Text strong>{t(`cashFlow.fields.${name}`)}</Text>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
          {t(`cashFlow.hints.${name}`)}
        </div>
        {control}
      </div>
    </Col>
  );

  return (
    <div>
      <Row gutter={16}>
        {field(
          'groupName',
          <Input value={form.groupName} onChange={(e) => update({ groupName: e.target.value })} />
        )}
        {field(
          'ownerName',
          <Input value={form.ownerName} onChange={(e) => update({ ownerName: e.target.value })} />
        )}
        {LIST_FIELDS.map((name) =>
          field(
            name,
            <Select
              mode="tags"
              style={{ width: '100%' }}
              open={false}
              suffixIcon={null}
              tokenSeparators={[',', ' ']}
              value={form[name] || []}
              onChange={(value) => update({ [name]: value })}
            />
          )
        )}
        {field(
          'vatGross',
          <Switch checked={form.vatGross} onChange={(vatGross) => update({ vatGross })} />
        )}
      </Row>
      <Space>
        <Button
          type="primary"
          className="btn-primary"
          disabled={!dirty}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('cashFlow.save')}
        </Button>
        {dirty && <Text type="warning">{t('fccs.unsaved')}</Text>}
      </Space>
    </div>
  );
}
